import Link from "next/link";
import { Container } from "@/components/ui/Container";
import { Eyebrow } from "@/components/ui/Eyebrow";
import { getTrade } from "@/lib/construction/trades";

type ServiceLinksProps = {
  /** Place name used in the heading, e.g. "Manchester" or "the North West". */
  place: string;
  /** Trade slugs found on the page; the first few link to their trade landing pages. */
  trades?: string[];
};

/**
 * Feeds the directory into the money pages: every directory page links to the
 * "web design for construction companies" service page and the matching trade pages.
 */
export function ServiceLinks({ place, trades = [] }: ServiceLinksProps) {
  const industries = Array.from(
    new Map(
      trades
        .map((slug) => getTrade(slug))
        .filter((t): t is NonNullable<ReturnType<typeof getTrade>> => Boolean(t))
        .map((t) => [t.industry, t]),
    ).values(),
  ).slice(0, 4);

  return (
    <section id="get-a-website" aria-labelledby="get-a-website-heading" className="bg-ink py-16 text-white md:py-20">
      <Container>
        <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <Eyebrow tone="inverse">For construction companies</Eyebrow>
            <h2 id="get-a-website-heading" className="mt-4 font-display text-display-lg font-bold text-white text-balance">
              Run a construction company in {place}?
            </h2>
            <p className="mt-5 max-w-2xl text-lg text-white/75">
              ConstructionWebDesign.uk builds websites for construction companies and trades for a fixed £1,000, live in 7
              days, with on-page local SEO and no monthly fees. See our{" "}
              <Link href="/construction-web-design" className="font-semibold text-white underline decoration-accent decoration-2 underline-offset-4">
                web design for construction companies
              </Link>
              , or check the{" "}
              <Link href="/pricing" className="font-semibold text-white underline decoration-accent decoration-2 underline-offset-4">
                construction website cost
              </Link>
              .
            </p>
          </div>
          {industries.length ? (
            <ul className="grid gap-3 sm:grid-cols-2 lg:col-span-5">
              {industries.map((t) => (
                <li key={t.industry}>
                  <Link
                    href={`/industries/${t.industry}`}
                    className="group flex h-full items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-sm font-semibold text-white transition-colors hover:border-accent hover:bg-white/10"
                  >
                    Website design for {t.plural}
                    <span aria-hidden="true" className="text-accent transition-transform group-hover:translate-x-1">
                      →
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </Container>
    </section>
  );
}
